import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronLeft, ChevronRight, Play, Heart, MessageCircle } from 'lucide-react';
import api, { mediaUrl } from '../lib/api';

const URL_RE = /https?:\/\/[^\s<>"')]+/gi;
const VIDEO_RE = /youtube\.com\/(watch|shorts|embed|live)|youtu\.be\/|fb\.watch\/|facebook\.com\/.*(\/videos\/|\/watch|\/reel\/|\/share\/[vr]\/)|instagram\.com\/(p|reels?|tv)\/|tiktok\.com\//i;
const ytId = (u) => (u.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([\w-]{6,})/i) || [])[1];

const Thumb = ({ post }) => {
  const url = (post.content.match(URL_RE) || [])[0];
  const [img, setImg] = useState(() => (post.image_urls?.[0] ? mediaUrl(post.image_urls[0]) : url && ytId(url) ? `https://img.youtube.com/vi/${ytId(url)}/hqdefault.jpg` : null));
  useEffect(() => {
    if (img || !url) return;
    api.get('/community/link-preview', { params: { url } }).then((r) => r.data?.image && setImg(r.data.image)).catch(() => {});
  }, [img, url]);
  const isVideo = url && VIDEO_RE.test(url);
  return (
    <div className="relative aspect-[4/3] bg-cream-2 overflow-hidden">
      {img ? <img src={img} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        : <div className="w-full h-full flex items-center justify-center p-5 font-italic-display text-rose-italic text-xl text-center line-clamp-4">{post.author_name}</div>}
      {isVideo && <span className="absolute inset-0 flex items-center justify-center"><span className="w-12 h-12 rounded-full bg-black/55 flex items-center justify-center"><Play className="w-5 h-5 text-white fill-white" /></span></span>}
    </div>
  );
};

const Card = ({ post }) => (
  <Link to="/community" data-testid={`home-community-post-${post.id}`}
    className="group snap-start shrink-0 w-[260px] md:w-[300px] rounded-2xl overflow-hidden bg-white/80 border border-rose/40 hover:shadow-lg transition-shadow">
    <Thumb post={post} />
    <div className="p-4">
      <div className="text-xs uppercase tracking-widest text-mahogany/60 truncate">{post.author_name}</div>
      <p className="mt-1.5 text-sm text-burgundy/85 line-clamp-3 min-h-[3.75rem]">{post.content.replace(URL_RE, '').trim() || 'Shared a post'}</p>
      <div className="mt-3 flex items-center gap-4 text-xs text-mahogany/60">
        <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5" />{post.like_count || 0}</span>
        <span className="flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" />{post.comment_count || 0}</span>
      </div>
    </div>
  </Link>
);

const CommunityStrip = () => {
  const [posts, setPosts] = useState([]);
  const ref = useRef(null);
  useEffect(() => { api.get('/community/posts', { params: { limit: 12 } }).then((r) => setPosts(r.data || [])).catch(() => {}); }, []);
  if (!posts.length) return null;
  const scroll = (d) => ref.current?.scrollBy({ left: d * 320, behavior: 'smooth' });
  return (
    <section className="py-20 md:py-24" data-testid="home-community-strip">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <span className="eyebrow">Community</span>
            <h2 className="mt-3 font-serif-display text-burgundy text-4xl md:text-6xl leading-[1.05]">From the <span className="font-italic-display text-rose-italic">Sangha</span></h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => scroll(-1)} aria-label="Previous" data-testid="home-community-prev" className="w-10 h-10 rounded-full border border-rose/50 flex items-center justify-center hover:bg-rose-soft"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => scroll(1)} aria-label="Next" data-testid="home-community-next" className="w-10 h-10 rounded-full border border-rose/50 flex items-center justify-center hover:bg-rose-soft"><ChevronRight className="w-4 h-4" /></button>
            <Link to="/community" className="pill-btn-outline ml-2" data-testid="home-community-viewall">Open feed <ArrowUpRight className="w-4 h-4" /></Link>
          </div>
        </div>
        <div ref={ref} className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-3 scrollbar-hide" data-testid="home-community-scroller">
          {posts.map((p) => <Card key={p.id} post={p} />)}
        </div>
      </div>
    </section>
  );
};

export default CommunityStrip;
